import { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, List, Modal, Skeleton, Space, Typography } from 'antd';

import GroupHeader from '../common/GroupHeader';
import { enableAutomation, previewAutomation } from '../../services/automations';
import { getNotificationDefs, previewNotificationDef, updateNotificationDef } from '../../services/notifications';
import { listApiKeys } from '../../services/apiKeys';
import { listGrants } from '../../services/oauth';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';

const { Text } = Typography;

/**
 * "Waiting for you" (KPR-196, epic KPE-001): what an assistant or an API key
 * made — or changed, or a setup brought — that arrived PAUSED because it
 * notifies people, changes other records, creates records, runs on a
 * schedule or tags. keepr turns one on only for the person, signed in
 * (`awaitingPerson`; a key is refused `person_must_enable`), so this is where
 * it happens: each row says what it does and who made it, and Turn on shows
 * what it would do before it switches — never the switch alone.
 *
 * Two panels share the parts: rules on the Automations tab, notifications on
 * the Notifications tab.
 */

/** keepr's sentence, read as plain text: its `*emphasis*` marks are dropped. */
export const plainSummary = text => String( text || '' ).replace( /\*/g, '' );

const machine = v => v?.kind === 'api-key' || v?.kind === 'oauth';
const sameVia = ( a, b ) => a?.kind === b?.kind && normalizeId( a?.keyId ) === normalizeId( b?.keyId ) && normalizeId( a?.grantId ) === normalizeId( b?.grantId );

/**
 * Who made it, as the person can know it: "Made by your API key "Fuel
 * import"", "Changed by your assistant "Claude"", "Made by an API key" (a
 * colleague's key is not theirs to list), "Made through a setup" (a person
 * applied one), or null when keepr did not record it (an automatic tag's
 * rule) — the row then says only when.
 */
export function madeByText( rule, keysById = {}, grantsById = {} ) {
	const changed = machine( rule?.updatedVia ) && ! sameVia( rule.updatedVia, rule?.createdVia );
	const via = changed ? rule.updatedVia : rule?.createdVia;
	const verb = changed ? 'Changed' : 'Made';
	if ( via?.kind === 'api-key' ) {
		const key = keysById[ normalizeId( via.keyId ) ];
		return key?.name ? `${ verb } by your API key "${ key.name }"` : `${ verb } by an API key`;
	}
	if ( via?.kind === 'oauth' ) {
		const grant = grantsById[ normalizeId( via.grantId ) ];
		const name = grant?.name || grant?.clientName;
		return name ? `${ verb } by your assistant "${ name }"` : `${ verb } by a connected assistant`;
	}
	if ( via?.kind === 'session' && rule?.kind !== 'tag-rule' ) return 'Made through a setup';
	return null;
}

/** The person's own keys and assistants, by id — what names a maker. */
function useMakers( enabled ) {
	const [ keysById, setKeys ] = useState( {} );
	const [ grantsById, setGrants ] = useState( {} );
	useEffect( () => {
		if ( ! enabled ) return undefined;
		let active = true;
		const index = rows => Object.fromEntries( ( rows || [] ).map( row => [ normalizeId( row?._id ), row ] ) );
		// Through a promise of their own, so a missing answer is an empty list, never a throw.
		Promise.resolve().then( () => listApiKeys( { includeRevoked: true } ) ).then( rows => { if ( active ) setKeys( index( rows ) ); }, () => {} );
		Promise.resolve().then( () => listGrants( { includeRevoked: true } ) ).then( rows => { if ( active ) setGrants( index( rows ) ); }, () => {} );
		return () => { active = false; };
	}, [ enabled ] );
	return { keysById, grantsById };
}

/** What a rule would do, from the rule preview (KPR-189). */
export function RuleFacts( { preview } ) {
	if ( ! preview ) return null;
	const facts = [];
	( preview.actions || [] ).forEach( action => {
		if ( typeof action?.matches === 'number' ) facts.push( `${ action.matches } record${ action.matches === 1 ? '' : 's' } match today` );
		else if ( action?.message ) facts.push( action.message );
		if ( typeof action?.recipientCount === 'number' ) facts.push( `It notifies ${ action.recipientCount } ${ action.recipientCount === 1 ? 'person' : 'people' }${ action.title ? `: "${ action.title }"` : '' }` );
	} );
	const sources = preview.generate?.sources;
	if ( typeof sources?.matches === 'number' ) facts.push( `Each run makes work for ${ sources.matches } record${ sources.matches === 1 ? '' : 's' }${ sources.truncated ? ` (of ${ sources.total })` : '' }` );
	( preview.arrives?.reasons || [] ).forEach( reason => { if ( reason?.message ) facts.push( reason.message ); } );
	const runs = ( preview.nextRuns || [] ).map( run => run?.local ).filter( Boolean );
	if ( runs.length ) facts.push( `Next runs: ${ runs.join( ', ' ) }` );
	return (
		<Space direction='vertical' size={8} style={{ width: '100%' }} data-testid='waiting-rule-preview'>
			{ preview.summary ? <Text>{plainSummary( preview.summary )}</Text> : null }
			{ facts.map( ( fact, index ) => <Text key={index} type='secondary'>{fact}</Text> ) }
			{ ( preview.warnings || [] ).map( ( warning, index ) => <Text key={`w${ index }`} type='warning'>{warning?.message}</Text> ) }
		</Space>
	);
}

/**
 * Turn on: what it would do, then the switch. `load` reads the preview (null
 * when there is none to read, as for an automatic tag's rule); a preview that
 * fails leaves Turn on off — nothing is switched on unseen (the review's F1,
 * F2) — and says why.
 */
export function TurnOnModal( { title, load, render, confirm, onClose, onDone, note = null } ) {
	const { message } = App.useApp();
	const [ preview, setPreview ] = useState( undefined );
	const [ error, setError ] = useState( null );
	const [ saving, setSaving ] = useState( false );

	useEffect( () => {
		let active = true;
		if ( ! load ) { setPreview( null ); return undefined; }
		load().then(
			data => { if ( active ) setPreview( data || {} ); },
			err => {
				if ( ! active ) return;
				const body = err?.response?.data || {};
				setError( body.code === 'email_unverified'
					? 'Confirm your email address first: keepr asks for it before a rule that notifies people is turned on. The link to resend it is on your profile.'
					: ( body.message || 'This could not be previewed, so it is not turned on from here. Open it with Review instead.' ) );
			}
		);
		return () => { active = false; };
	}, [ load ] );

	const turnOn = async () => {
		setSaving( true );
		try {
			await confirm();
			message.success( 'Turned on.' );
			onDone();
		}
		catch ( err ) {
			message.error( err?.response?.data?.message || 'Unable to turn it on right now. Please try again.' );
			setSaving( false );
		}
	};

	return (
		<Modal
			open
			title={title}
			okText='Turn on'
			onOk={turnOn}
			okButtonProps={{ disabled: Boolean( error ) || preview === undefined, loading: saving }}
			onCancel={onClose}
		>
			{ error ? <Text type='secondary'>{error}</Text>
				: preview === undefined ? <Skeleton active paragraph={{ rows: 3 }} />
					: <Space direction='vertical' size={8} style={{ width: '100%' }}>{ note ? <Text>{note}</Text> : null }{ render( preview ) }</Space> }
		</Modal>
	);
}

/** Turn on one rule — shared with the list's own switch, which never switches a waiting rule unseen (F8). */
export function TurnOnRuleModal( { collectionId, rule, nameOf = r => r?.name, onClose, onDone } ) {
	const id = normalizeId( rule?._id );
	const tagRule = rule?.kind === 'tag-rule';
	const load = useCallback( () => previewAutomation( collectionId, { automationId: id } ), [ collectionId, id ] );
	return (
		<TurnOnModal
			title={`Turn on "${ nameOf( rule ) || 'Untitled rule' }"?`}
			// An automatic tag's rule has no rule preview (it belongs to its tag):
			// what it does is said, and its tag's own window shows what it tags.
			load={tagRule ? null : load}
			note={tagRule ? 'It puts its tag on every record its rule holds for, and keeps it there — starting now, records already here included.' : null}
			render={preview => ( preview ? <RuleFacts preview={preview} /> : null )}
			confirm={() => enableAutomation( collectionId, id )}
			onClose={onClose}
			onDone={onDone}
		/>
	);
}

/**
 * What one waiting rule's note says, by the class keepr gave it (`arrives.class`,
 * KPR-1254): a harmless rule only sets values on its own record, so it is not
 * "only you" — an assistant or an API key may turn it on too; a noisy or
 * destructive one is the person's alone (its reasons print on the row). A rule
 * with no class (an older API) keeps the sentence it always had.
 */
export function ruleNote( rule ) {
	const cls = rule?.arrives?.class;
	if ( cls === 'harmless' ) return 'It only sets values on the record that sets it off, and it arrived paused so nothing ran before you saw it. You can turn it on here, and so can an assistant or an API key.';
	if ( cls === 'noisy' || cls === 'destructive' ) return 'Only you can turn this on.';
	return 'Only you can turn this on. An assistant, an API key or a setup made it, and it acts beyond the record that sets it off.';
}

function WaitingList( { label, rows, nameOf, keysById, grantsById, reasonsOf, noteOf, onTurnOn, onReview } ) {
	return (
		<section className='waiting-rules' data-testid='waiting-rules' aria-label={label}>
			<GroupHeader as='h4' label={label} count={rows.length} />
			{ noteOf ? null : <p className='waiting-rules__note meta-line'>
				Only you can turn {rows.length === 1 ? 'this' : 'these'} on. An assistant, an API key or a setup made {rows.length === 1 ? 'it' : 'them'}, and {rows.length === 1 ? 'it acts' : 'they act'} beyond the record that sets {rows.length === 1 ? 'it' : 'them'} off.
			</p> }
			<List
				bordered
				dataSource={rows}
				rowKey={row => normalizeId( row?._id )}
				renderItem={row => {
					const made = madeByText( row, keysById, grantsById );
					const when = row?.updatedAt || row?.createdAt;
					const reasons = reasonsOf( row );
					return (
						<List.Item key={normalizeId( row?._id )} className='waiting-rules__row'>
							<Space direction='vertical' size={4} style={{ width: '100%' }}>
								<Text strong>{nameOf( row ) || 'Untitled'}</Text>
								{ row?.summary ? <Text>{plainSummary( row.summary )}</Text> : null }
								{ reasons.length ? <Text type='secondary'>{reasons.join( ' ' )}</Text> : null }
								{ noteOf ? <Text type='secondary' className='meta-line waiting-rules__row-note'>{noteOf( row )}</Text> : null }
								<Text type='secondary' className='meta-line'>
									{ [ made, when ? `${ made ? '' : 'Waiting since ' }${ formatDisplayDateTime( when ) }` : null ].filter( Boolean ).join( ' · ' ) }
								</Text>
								<Space size={8} wrap className='waiting-rules__actions'>
									<Button onClick={() => onTurnOn( row )}>Turn on</Button>
									{ onReview ? <Button onClick={() => onReview( row )}>Review</Button> : null }
								</Space>
							</Space>
						</List.Item>
					);
				}}
			/>
		</section>
	);
}

/** Settings › Automations: the rules waiting for the person. */
export default function WaitingRules( { collectionId, rules = [], nameOf = r => r?.name, onReview, onChanged } ) {
	// A row turned on here leaves at once, before the list reads again (F11).
	const [ done, setDone ] = useState( () => new Set() );
	const waiting = useMemo(
		() => rules.filter( rule => rule?.awaitingPerson && rule?.enabled === false && ! done.has( normalizeId( rule?._id ) ) ),
		[ rules, done ]
	);
	const { keysById, grantsById } = useMakers( waiting.length > 0 );
	const [ turningOn, setTurningOn ] = useState( null );

	if ( ! waiting.length ) return null;
	return (
		<>
			<WaitingList
				label='Waiting for you'
				rows={waiting}
				nameOf={nameOf}
				keysById={keysById}
				grantsById={grantsById}
				noteOf={ruleNote}
				reasonsOf={rule => ( rule?.arrives?.reasons || [] ).map( reason => reason?.message ).filter( Boolean )}
				onTurnOn={setTurningOn}
				onReview={onReview}
			/>
			{ turningOn ? (
				<TurnOnRuleModal
					collectionId={collectionId}
					rule={turningOn}
					nameOf={nameOf}
					onClose={() => setTurningOn( null )}
					onDone={() => {
						const rule = turningOn;
						setDone( prev => new Set( [ ...prev, normalizeId( rule?._id ) ] ) );
						setTurningOn( null );
						onChanged( rule );
					}}
				/>
			) : null }
		</>
	);
}

/**
 * Settings › Notifications: the collection's notifications waiting for the
 * person. A notification has no switch of its own on that tab, so this is
 * the one place one that arrived paused is turned on (the review's guide
 * finding). Turn on shows its rendered preview, then `{ enabled: true }`.
 */
export function WaitingNotifications( { collectionId, onChanged } ) {
	const [ defs, setDefs ] = useState( [] );
	const [ turningOn, setTurningOn ] = useState( null );
	const reload = useCallback( () => {
		if ( ! collectionId ) return Promise.resolve();
		return Promise.resolve().then( () => getNotificationDefs( { tier: 'collection', collectionId } ) ).then(
			answer => setDefs( ( answer?.defs || [] ).filter( def => def?.scope === 'collection'
				&& normalizeId( def?.collection_id ) === normalizeId( collectionId ) && def?.awaitingPerson && def?.enabled === false ) ),
			() => setDefs( [] )
		);
	}, [ collectionId ] );
	useEffect( () => { reload(); }, [ reload ] );
	const { keysById, grantsById } = useMakers( defs.length > 0 );
	const load = useCallback(
		() => ( turningOn ? previewNotificationDef( normalizeId( turningOn._id ), { collection_id: collectionId } ) : Promise.resolve( null ) ),
		[ turningOn, collectionId ]
	);

	if ( ! defs.length ) return null;
	return (
		<>
			<WaitingList
				label='Waiting for you'
				rows={defs}
				nameOf={def => def?.name}
				keysById={keysById}
				grantsById={grantsById}
				reasonsOf={def => [ def?.template?.title ? `It says "${ def.template.title }".` : null ].filter( Boolean )}
				onTurnOn={setTurningOn}
			/>
			{ turningOn ? (
				<TurnOnModal
					title={`Turn on "${ turningOn?.name || 'Untitled notification' }"?`}
					load={load}
					render={preview => (
						<Space direction='vertical' size={8} style={{ width: '100%' }}>
							{ preview?.title ? <Text strong>{preview.title}</Text> : null }
							{ preview?.body ? <Text>{plainSummary( preview.body )}</Text> : null }
							{ typeof preview?.recipientCount === 'number' ? <Text type='secondary'>{`It reaches ${ preview.recipientCount } ${ preview.recipientCount === 1 ? 'person' : 'people' }.`}</Text> : null }
						</Space>
					)}
					confirm={() => updateNotificationDef( normalizeId( turningOn._id ), { enabled: true } )}
					onClose={() => setTurningOn( null )}
					onDone={() => { setTurningOn( null ); reload(); if ( onChanged ) onChanged(); }}
				/>
			) : null }
		</>
	);
}
